import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, captureSalesRef, fmt, rememberManagementToken, reportSalesVisit, type CouponResultDto, type EventDto, type EventTeaserDto } from '../api';
import { PreviewBanner } from '../components/PreviewBanner';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePreviewPassCapture } from '../hooks/usePreviewPass';

interface Summary {
  lines: { id: string; label: string; amt: number }[];
  subtotal: number;
  couponDiscount: number;
  total: number;
  qty: number;
  entries: number;
}

/**
 * The breakpoint at which the hero switches to its portrait crop.
 *
 * Written once and used twice — in the `<source media>` below and in `.hero-media--portrait`'s
 * media query — because the two must agree exactly. If the CSS switched the frame to 4:5 at a width
 * where `<picture>` was still handing over the landscape file, a phone would get a 21:9 photograph
 * letterboxed inside a portrait box, which is worse than either crop on its own.
 */
const HERO_PORTRAIT_MEDIA = '(max-width: 640px)';

/**
 * The hero image, in whichever crop fits the screen it is being read on.
 *
 * `<picture>` rather than a width measured in JavaScript: the browser resolves it before first
 * paint and re-resolves it on rotation, on a window drag, and on a tablet splitting its screen —
 * none of which a `useState` of `window.innerWidth` gets right without a resize listener and a
 * frame of the wrong image first. It also means only the chosen file is ever downloaded.
 *
 * With no portrait upload this renders exactly what it always did: one `<img>` of the landscape
 * hero, in a 21:9 frame, on every device.
 */
function HeroMedia({ src, portraitSrc, alt }: { src: string; portraitSrc: string | null; alt: string }) {
  return (
    <div className={`hero-media${portraitSrc ? ' hero-media--portrait' : ''}`}>
      <picture>
        {portraitSrc && <source media={HERO_PORTRAIT_MEDIA} srcSet={portraitSrc} />}
        <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </picture>
    </div>
  );
}

/** Little iOS-calendar-style date badge: weekday + month on the colored strip, day number below. */
function CalendarChip({ date }: { date: Date }) {
  const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
  const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
  return (
    <span className="calendar-chip">
      <span className="calendar-chip-head">
        <span>{weekday}</span>
        <span>{month}</span>
      </span>
      <span className="calendar-chip-day">{date.getDate()}</span>
    </span>
  );
}

function computeSummary(event: EventDto, cart: Record<string, number>, coupon: CouponResultDto | null): Summary {
  let subtotal = 0, qty = 0, entries = 0;
  const lines: Summary['lines'] = [];
  for (const t of event.tickets) {
    const q = cart[t.id] || 0;
    if (!q) continue;
    subtotal += t.price * q;
    qty += q;
    entries += q * t.seats;
    lines.push({ id: t.id, label: `${t.en} × ${q}`, amt: t.price * q });
  }
  const couponDiscount = coupon && qty > 0 ? Math.min(subtotal, coupon.discount) : 0;
  return { lines, subtotal, couponDiscount, total: Math.max(0, subtotal - couponDiscount), qty, entries };
}

/**
 * TICK-081 — the page an announced-but-unreleased event shows at its own URL.
 *
 * Everything it can say is in `teaser`; there is no ticket state, no cart and no way to reach one,
 * because the payload behind it carries none. The lineup is absent on purpose — it names artists
 * the organizer has not announced, which is the reason this page exists at all.
 */
function ComingSoon({ teaser, eventSlug }: { teaser: EventTeaserDto; eventSlug?: string }) {
  return (
    <div className="page">
      <PreviewBanner eventSlug={eventSlug} />
      <header className="site-header shell">
        <div className="logo">{teaser.name}</div>
        <nav className="site-nav">
          <span className="mono-label" style={{ fontSize: 11 }}>ยังไม่เปิดจำหน่าย</span>
        </nav>
      </header>

      <section className="hero shell container coming-soon">
        <span className="coming-soon-chip">
          <span className="coming-soon-dot" aria-hidden="true" />
          COMING SOON
        </span>
        {teaser.eyebrow && <div className="mono-label coming-soon-eyebrow">{teaser.eyebrow}</div>}
        <h1>{teaser.headline}</h1>
        {teaser.message && <p className="coming-soon-message">{teaser.message}</p>}
        {teaser.ctaUrl && (
          <div className="hero-cta-row">
            <a className="btn-ghost" href={teaser.ctaUrl} target="_blank" rel="noreferrer noopener">
              {teaser.ctaLabel}
            </a>
          </div>
        )}
        {teaser.heroImageUrl && (
          <HeroMedia src={teaser.heroImageUrl} portraitSrc={teaser.heroImagePortraitUrl} alt={teaser.name} />
        )}
        <div className="facts-grid coming-soon-facts">
          {teaser.dateLabel && (
            <div className="fact">
              <div className="k">DATE</div>
              <div className="v">{teaser.dateLabel}</div>
            </div>
          )}
          <div className="fact">
            <div className="k">TICKETS</div>
            <div className="v">ประกาศเร็ว ๆ นี้</div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { eventSlug } = useParams<{ eventSlug?: string }>();
  const prefix = eventSlug ? `/e/${eventSlug}` : '';
  const [params, setParams] = useSearchParams();
  const [event, setEvent] = useState<EventDto | null>(null);
  const [teaser, setTeaser] = useState<EventTeaserDto | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [cart, setCart] = useState<Record<string, number>>({});
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState<CouponResultDto | null>(null);
  const [couponMsg, setCouponMsg] = useState('');
  const [reserveError, setReserveError] = useState('');
  const [reserving, setReserving] = useState(false);
  const expired = params.get('expired') === '1';
  const ticketsRef = useRef<HTMLElement | null>(null);

  usePageTitle(eventSlug, 'Tickets');

  /*
   * TICK-081 — a preview pass arrives in the URL exactly once. Called above the load effect so the
   * pass is stored before the first request goes out. The hook is shared with `Checkout` and
   * `OrderStatus`, which need it for the same reason and used not to have it.
   */
  usePreviewPassCapture(eventSlug);

  /*
   * TICK-081 — three outcomes where there used to be one.
   *
   * A 404 from `/event` means either "no such event" or "hidden", deliberately indistinguishable.
   * `/teaser` is the only thing that can tell them apart, and only for an event whose organizer
   * opted in — so it is asked second, and its own 404 leaves the hidden case looking exactly like
   * the missing one, as it did before.
   */
  useEffect(() => {
    // TICK-084 — a redirect_to_line arrival leaves this page before it ever renders the ticket
    // page, via reportSalesVisit's own window.location.replace below. Fetching event data here
    // anyway just races that navigation: the browser cancels the in-flight request mid-redirect,
    // and the cancellation surfaces as a generic fetch failure that flashes the loadError message.
    // TICK-095 — from_line is the same navigation in the other direction, and races it the same way.
    const bouncing = params.get('redirect_to_line') === 'true' || params.get('from_line') === 'true';
    if ((params.get('ref') ?? '').trim() && bouncing) return;
    let cancelled = false;
    setEvent(null);
    setTeaser(null);
    setNotFound(false);
    setLoadError('');
    api.getEvent(eventSlug)
      .then((loaded) => { if (!cancelled) setEvent(loaded); })
      .catch(async (err) => {
        if (cancelled) return;
        if (!(err instanceof ApiError) || err.status !== 404) {
          setLoadError('ไม่สามารถโหลดข้อมูลงานได้ กรุณารีเฟรชหน้า / Failed to load event');
          return;
        }
        try {
          const upcoming = await api.getEventTeaser(eventSlug);
          if (!cancelled) setTeaser(upcoming);
        } catch {
          if (!cancelled) setNotFound(true);
        }
      });
    return () => { cancelled = true; };
  }, [eventSlug]);

  /*
   * TICK-075 — remember which link brought this visitor, for the order they may place later.
   *
   * Runs on every search-string change rather than once on mount, because last touch means a second
   * arrival through a different creator's link has to overwrite the first — and within a SPA that
   * arrival is a param change, not a remount.
   */
  useEffect(() => {
    captureSalesRef(window.location.search);
    // TICK-084 — count the arrival, and take the LINE detour when the link asked for one. Same
    // trigger as the capture above for the same reason: a second arrival is a param change here.
    reportSalesVisit(eventSlug, window.location.search);
  }, [params, eventSlug]);

  const summary = useMemo(
    () => (event ? computeSummary(event, cart, coupon) : null),
    [event, cart, coupon],
  );

  if (teaser) return <ComingSoon teaser={teaser} eventSlug={eventSlug} />;
  if (notFound) return <div className="page shell" style={{ paddingTop: 80 }}>ไม่พบงานนี้ / Event not found</div>;
  if (loadError) return <div className="page shell" style={{ paddingTop: 80 }}>{loadError}</div>;
  if (!event || !summary) return <div className="page" />;

  const setQty = (id: string, delta: number, max: number) => {
    setCart((c) => ({ ...c, [id]: Math.max(0, Math.min(max, (c[id] || 0) + delta)) }));
    if (coupon) {
      setCoupon(null);
      setCouponMsg('ตะกร้าเปลี่ยนแล้ว กรุณาใช้โค้ดอีกครั้ง');
    }
  };

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    try {
      const result = await api.validateCoupon(code, cart, eventSlug);
      setCoupon(result);
      setCouponMsg(result.message);
    } catch (err) {
      setCoupon(null);
      setCouponMsg(err instanceof ApiError ? err.message : 'ตรวจสอบโค้ดไม่สำเร็จ');
    }
  };

  const reserve = async () => {
    if (!summary.qty || reserving) return;
    setReserving(true);
    setReserveError('');
    try {
      const order = await api.createOrder(cart, coupon?.code, eventSlug);
      sessionStorage.setItem('jamtick.orderId', order.id);
      // TICK-060: the only moment this value is ever sent to a client. Storing it here — rather
      // than after payment — is what lets the customer manage delivery while the slip is still
      // being checked, which is the whole point of the two states being independent.
      rememberManagementToken(order.code, order.managementToken);
      navigate(`${prefix}/checkout/${order.id}`);
    } catch (err) {
      setReserveError(err instanceof ApiError ? err.message : 'จองบัตรไม่สำเร็จ กรุณาลองใหม่');
      api.getEvent(eventSlug).then(setEvent).catch(() => {});
    } finally {
      setReserving(false);
    }
  };

  return (
    <div className="page">
      <PreviewBanner eventSlug={eventSlug} />
      <header className="site-header sticky shell">
        <div className="logo">{event.name}</div>
        <nav className="site-nav">
          <a href="#lineup">LINEUP</a>
          <a href="#tickets">TICKETS</a>
          {event.phase === 'early-bird' && (
            <span className="early-bird-badge">
              <span className="mono-tag early-bird-tag">EARLY BIRD</span>
              {/*
                The date is the organizer's to publish or not (migration 0055). Hiding it leaves the
                badge saying the true thing — a discount is running now — without committing the
                event to a public deadline it may still move. The cutoff itself is unaffected.
              */}
              {event.earlyBirdDateVisible && <CalendarChip date={new Date(event.earlyBirdUntil)} />}
            </span>
          )}
        </nav>
      </header>

      {expired && (
        <div className="notice-bar">
          หมดเวลาการจอง — บัตรถูกปล่อยกลับเข้าระบบแล้ว กรุณาเลือกบัตรใหม่อีกครั้ง / Reservation expired.{' '}
          <button className="btn-link" onClick={() => setParams({})}>ปิด ✕</button>
        </div>
      )}

      <section className="hero shell container">
        <div className="mono-label" style={{ letterSpacing: '0.2em', marginBottom: 20 }}>{event.tagline}</div>
        <h1>{event.name}</h1>
        <div className="hero-cta-row">
          <button
            className="btn-primary"
            onClick={() => ticketsRef.current?.scrollIntoView({ behavior: 'smooth' })}
          >
            ซื้อบัตร / GET TICKETS
          </button>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>{event.earlyBirdNote}</span>
        </div>
        {event.heroImageUrl ? (
          <HeroMedia src={event.heroImageUrl} portraitSrc={event.heroImagePortraitUrl} alt={event.name} />
        ) : (
          <div className="hero-media">
            <span className="media-caption">MOONFIELD · KHAO YAI · TWO NIGHTS UNDER THE MOON</span>
          </div>
        )}
        <div className="facts-grid">
          {event.facts.map((f) => (
            <div className="fact" key={f.k}>
              <div className="k">{f.k}</div>
              <div className="v">{f.v}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="lineup" className="section shell container">
        <div className="section-head">
          <h2>LINEUP</h2>
          <span className="meta">{event.lineupDays} DAYS · {event.lineup.length} ACTS</span>
        </div>
        <div className="lineup-grid">
          {event.lineup.map((a, i) => (
            <div className="artist" key={a.name}>
              <div className="artist-photo">
                {a.photoUrl ? (
                  <img src={a.photoUrl} alt={a.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span>{String(i + 1).padStart(2, '0')}</span>
                )}
              </div>
              <div>
                <div className="artist-name">{a.name}</div>
                <div className="artist-time">{a.time}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="tickets" ref={ticketsRef} className="section shell container" style={{ paddingBottom: 120 }}>
        <div className="section-head" style={{ marginBottom: 12 }}>
          <h2>TICKETS</h2>
          {event.ticketsTaglineVisible && event.ticketsTagline && (
            <span className="meta">{event.ticketsTagline}</span>
          )}
        </div>

        <div className="tickets-layout">
          <div className="ticket-list">
            {event.tickets.map((t) => {
              const q = cart[t.id] || 0;
              return (
                <div className="ticket-card" key={t.id}>
                  <div className="ticket-info">
                    <div className="ticket-title-row">
                      <span className="ticket-title">{t.en}</span>
                      {t.promotion && <span className="promotion-badge">โปรโมชั่น</span>}
                    </div>
                    <div className="ticket-sub">{t.th}</div>
                  </div>
                  <div className="ticket-buy">
                    <div className="price-block">
                      {event.phase === 'early-bird' && t.price < t.priceRegular && <div className="price-struck">{fmt(t.priceRegular)}</div>}
                      <div className="price-now">{fmt(t.price)}</div>
                      <div className="price-note">{t.seats > 1 ? `1 ใบ / ${t.seats} คน` : 'ต่อใบ / per ticket'}</div>
                    </div>
                    {t.soldOut ? (
                      <span className="soldout-tag">SOLD OUT</span>
                    ) : (
                      <div className="qty-stepper">
                        <button aria-label={`ลดจำนวน ${t.en}`} onClick={() => setQty(t.id, -1, t.maxPerOrder)}>−</button>
                        <span>{q}</span>
                        <button aria-label={`เพิ่มจำนวน ${t.en}`} onClick={() => setQty(t.id, 1, t.maxPerOrder)}>+</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <aside className="summary">
            <div className="mono-label" style={{ marginBottom: 18 }}>ORDER SUMMARY</div>
            {summary.qty === 0 ? (
              <div className="summary-empty">ยังไม่ได้เลือกบัตร — เลือกจำนวนบัตรจากรายการด้านซ้าย</div>
            ) : (
              <>
                <div className="summary-lines">
                  {summary.lines.map((l) => (
                    <div className="summary-line" key={l.id}>
                      <span className="lbl">{l.label}</span>
                      <span className="amt">{fmt(l.amt)}</span>
                    </div>
                  ))}
                  {summary.couponDiscount > 0 && coupon && (
                    <div className="summary-line">
                      <span className="lbl">โค้ด {coupon.code}</span>
                      <span className="amt">−{fmt(summary.couponDiscount)}</span>
                    </div>
                  )}
                </div>
                <div className="coupon-row">
                  <input
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                    placeholder="โค้ดส่วนลด / Promo code"
                  />
                  <button className="btn-ghost" onClick={applyCoupon}>ใช้โค้ด</button>
                </div>
                {couponMsg && <div className="coupon-msg">{couponMsg}</div>}
                <div className="summary-total">
                  <span className="info">รวม {summary.qty} ใบ · เข้างานได้ {summary.entries} คน</span>
                  <span className="amount">{fmt(summary.total)}</span>
                </div>
                <button className="btn-primary reserve-btn" onClick={reserve} disabled={reserving}>
                  {reserving ? 'กำลังจอง…' : 'จองบัตร / RESERVE'}
                </button>
                {reserveError && <div className="form-error">{reserveError}</div>}
                <div className="hold-note">
                  ระบบจะล็อกบัตรไว้ {event.holdMinutes} นาที ระหว่างโอนเงินและแนบสลิป
                </div>
              </>
            )}
          </aside>
        </div>
      </section>

      {event.sections.map((s) => (
        <section key={s.id} className="section shell container">
          <div className="section-head">
            <h2>{s.title}</h2>
          </div>
          {s.type === 'faq' ? (
            <div className="faq-list">
              {s.items.map((it, i) => (
                <div className="card" key={i} style={{ marginBottom: 12 }}>
                  <div className="card-step">{it.q}</div>
                  <div style={{ fontSize: 14, color: 'var(--muted)', whiteSpace: 'pre-wrap' }}>{it.a}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="lineup-grid">
              {s.items.map((it, i) => (
                <div className="artist" key={i}>
                  <div className="artist-photo">
                    {it.logoUrl ? (
                      <img src={it.logoUrl} alt={it.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <span>{it.name.slice(0, 2).toUpperCase()}</span>
                    )}
                  </div>
                  <div>
                    <div className="artist-name">
                      {it.url ? <a href={it.url} target="_blank" rel="noreferrer">{it.name}</a> : it.name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      ))}

      <footer className="site-footer shell">
        <span>{event.company}</span>
        <span className="mail">{event.contactEmail}</span>
      </footer>
    </div>
  );
}
